import React, { useMemo, useState } from 'react';
import Dropdown from 'components/Dropdown';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import LabelWrap from 'components/inputs/LabelWrap';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import Select from 'components/inputs/Select';
import useBusinessUnits from 'contexts/basicData/useBusinessUnits';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { ApprovalFilterEnum } from 'api/model';

const FilterWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

// circular badge
const FilterSpan = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.foreground.button};
  color: ${({ theme }) => theme.colors.background.button};
  font-size: 0.8em;
  font-weight: bold;
`;

const MyButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  height: 30px;
`;

const MyLabelWrap = styled(LabelWrap)<{ width?: number }>`
  height: 100%;
`;

const StyledMultiSelect = styled(MultiSelect)`
  ${({ value, theme }) =>
    value.size > 0 &&
    css`
      background: ${theme.colors.background.highlight};
    `}
` as typeof MultiSelect;

const StyledSelect = styled(Select)<{ $active: boolean }>`
  width: 100%;
  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.background.highlight};
    `}
`;

type ApprovalFilterDropdownProps = {
  businessUnitIDs?: number[];
  setBusinessUnitIDs: (ids: number[]) => void;
  approvalFilter: ApprovalFilterEnum;
  setApprovalFilter: (value: ApprovalFilterEnum) => void;
  onlyAutoApproveUsers: boolean;
  setOnlyAutoApproveUsers: (value: boolean) => void;
  showOnlyFullDayComplete: boolean;
  setShowOnlyFullDayComplete: (value: boolean) => void;
  excludeApprovedDays: boolean;
  setExcludeApprovedDays: (value: boolean) => void;
};

const ApprovalFilterDropdown: React.FC<ApprovalFilterDropdownProps> = ({
  businessUnitIDs = [],
  setBusinessUnitIDs,
  approvalFilter,
  setApprovalFilter,
  onlyAutoApproveUsers,
  setOnlyAutoApproveUsers,
  showOnlyFullDayComplete,
  setShowOnlyFullDayComplete,
  excludeApprovedDays,
  setExcludeApprovedDays,
}) => {
  const { unitsById } = useBusinessUnits();

  const [filtersOpen, setFiltersOpen] = useState(false);

  const businessUnitOptions = useMemo((): Option<number>[] => {
    return Array.from(unitsById.values()).map((unit) => ({
      label: unit.name,
      value: unit.id,
    }));
  }, [unitsById]);

  const numberOfFilters = useMemo(() => {
    let count = businessUnitIDs.length;
    if (approvalFilter !== ApprovalFilterEnum.All) count += 1;
    if (onlyAutoApproveUsers) count += 1;
    if (showOnlyFullDayComplete) count += 1;
    if (excludeApprovedDays) count += 1;
    return count;
  }, [
    businessUnitIDs.length,
    approvalFilter,
    onlyAutoApproveUsers,
    showOnlyFullDayComplete,
    excludeApprovedDays,
  ]);

  const clearAll = () => {
    setBusinessUnitIDs([]);
    setApprovalFilter(ApprovalFilterEnum.All);
    setOnlyAutoApproveUsers(false);
    setShowOnlyFullDayComplete(false);
    setExcludeApprovedDays(false);
  };

  return (
    <Dropdown
      onLostFocus={() => setFiltersOpen(false)}
      position="left"
      content={
        filtersOpen && (
          <FilterWrapper>
            <MyLabelWrap label="Enhet">
              <StyledMultiSelect
                onChange={(newVal) => {
                  setBusinessUnitIDs(Array.from(newVal));
                }}
                options={businessUnitOptions}
                value={new Set(businessUnitIDs)}
              />
            </MyLabelWrap>

            <MyLabelWrap label="Godkännandestatus">
              <StyledSelect
                $active={approvalFilter !== ApprovalFilterEnum.All}
                value={approvalFilter}
                onChange={(e) =>
                  setApprovalFilter(
                    Number(e.target.value) as ApprovalFilterEnum
                  )
                }
              >
                <option value={ApprovalFilterEnum.All}>Alla uppdrag</option>
                <option value={ApprovalFilterEnum.AutoApproved}>
                  Auto-godkända
                </option>
                <option value={ApprovalFilterEnum.ManuallyApproved}>
                  Manuellt godkända
                </option>
                <option value={ApprovalFilterEnum.NotYetApproved}>
                  Ej godkända än
                </option>
              </StyledSelect>
            </MyLabelWrap>

            <Checkbox
              checked={onlyAutoApproveUsers}
              label="Visa endast uppdrag från användare med auto-godkänning aktiverat"
              onChange={() =>
                setOnlyAutoApproveUsers(!onlyAutoApproveUsers)
              }
            >
              Visa endast uppdrag från användare med auto-godkänning aktiverat
            </Checkbox>

            <Checkbox
              checked={showOnlyFullDayComplete}
              label="Visa endast Fälttestare där dagens samtliga uppdrag är slutförda"
              onChange={() =>
                setShowOnlyFullDayComplete(!showOnlyFullDayComplete)
              }
            >
              Visa endast Fälttestare där dagens samtliga uppdrag är slutförda
            </Checkbox>

            <Checkbox
              checked={excludeApprovedDays}
              label="Exkludera Fälttestare vars dag är godkänd"
              onChange={() => setExcludeApprovedDays(!excludeApprovedDays)}
            >
              Exkludera Fälttestare vars dag är godkänd
            </Checkbox>

            <MyButton
              disabled={numberOfFilters === 0}
              onClick={clearAll}
            >
              Rensa
            </MyButton>
          </FilterWrapper>
        )
      }
    >
      <MyButton
        icon={<FontAwesomeIcon icon={faFilter} />}
        onClick={() => setFiltersOpen(!filtersOpen)}
      >
        <span>Filter </span>
        {numberOfFilters > 0 && <FilterSpan>{numberOfFilters}</FilterSpan>}
      </MyButton>
    </Dropdown>
  );
};

export default ApprovalFilterDropdown;
